import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { Box, CssBaseline, Typography } from '@mui/material';
import { Toaster } from 'react-hot-toast';
import { AppModeProvider } from './contexts/AppModeContext';
import { TranslationProvider } from './contexts/TranslationContext';
import { WebsiteSettingsProvider, useWebsiteSettings } from './contexts/WebsiteSettingsContext';
import FullScreenLoader from './components/Shared/FullScreenLoader';
import { getColorSchemeById } from './theme/colorSchemes';

// Components
import Layout from './components/Layout/Layout';
import HomePage from './pages/HomePage';
import ProductsPage from './pages/ProductsPage';
import ProductDetailPage from './pages/ProductDetailPage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import SellerDashboard from './pages/SellerDashboard';
import ProfilePage from './pages/ProfilePage';
import OrdersPage from './pages/OrdersPage';
import UserDashboard from './pages/UserDashboard';
import ProductUploadPage from './pages/ProductUploadPage';
import JobUploadPage from './pages/JobUploadPage';
import EditProductPage from './pages/EditProductPage';
import SellerProfilePage from './pages/SellerProfilePage';
import ProfileRouter from './pages/ProfileRouter';
import AllProductsPage from './pages/AllProductsPage';
import AdminDashboard from './pages/AdminDashboard';
import AdminLinkPage from './pages/AdminLinkPage';
import CategoriesPage from './pages/CategoriesPage';
import MessagesPage from './pages/MessagesPage';
import CompaniesPage from './pages/CompaniesPage';
import CompanyPage from './pages/CompanyPage';
import CreateCompanyPage from './pages/CreateCompanyPage';
import EditCompanyPage from './pages/EditCompanyPage';
import MembersPage from './pages/MembersPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import PaymentCancelledPage from './pages/PaymentCancelledPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsPage from './pages/TermsPage';
import RulesPage from './pages/RulesPage';
import KundencenterPage from './pages/KundencenterPage';
import SubscriptionsPlatformPurchasesPage from './pages/SubscriptionsPlatformPurchasesPage';
import PagesEditor from './pages/PagesEditor';
import PagePreview from './pages/PagePreview';
import PostEditor from './pages/post-editor';
import MediaGallery from './pages/MediaGallery';
import NotFoundPage from './pages/NotFoundPage';
import ComponentEditor from './pages/ComponentEditor';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import AdminLinkVerifierDialog from './components/Admin/AdminLinkVerifierDialog';
import TalentPoolPage from './pages/TalentPoolPage';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (renamed from cacheTime in v5)
    },
  },
});

const hexToRgb = (hex: string) => {
  const h = String(hex || '').replace('#', '').trim();
  if (h.length === 3) {
    const r = parseInt(h[0] + h[0], 16);
    const g = parseInt(h[1] + h[1], 16);
    const b = parseInt(h[2] + h[2], 16);
    return { r, g, b };
  }
  if (h.length === 6) {
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    return { r, g, b };
  }
  return { r: 14, g: 165, b: 233 };
};

// Redirect component for profile routes
const ProfileRedirect: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/user-profile/${id}`} replace />;
};

// Overlay that shows while website settings are loading
const SettingsOverlay: React.FC = () => {
  const { loading } = useWebsiteSettings();
  if (!loading) return null;
  return <FullScreenLoader text="Deine Erfahrung wird vorbereitet..." />;
};

const ThemedApp: React.FC = () => {
  const { settings } = useWebsiteSettings();
  const scheme = React.useMemo(() => getColorSchemeById(settings?.colorScheme), [settings?.colorScheme]);
  const theme = React.useMemo(() => {
    const rgb = hexToRgb(scheme.primary);
    return createTheme({
      palette: {
        primary: { main: scheme.primary },
        secondary: { main: scheme.secondary },
        background: { default: '#f7f9fc', paper: '#ffffff' },
      },
      typography: {
        fontFamily: 'Inter, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif',
        h1: { fontWeight: 800 },
        h2: { fontWeight: 800 },
        h3: { fontWeight: 700 },
        h4: { fontWeight: 700 },
        button: { fontWeight: 700, textTransform: 'none' },
      },
      shape: { borderRadius: 12 },
      components: {
        MuiCssBaseline: {
          styleOverrides: {
            body: {
              backgroundImage: `radial-gradient(1200px 600px at 0% -10%, rgba(${rgb.r},${rgb.g},${rgb.b},0.08), transparent), radial-gradient(1000px 600px at 100% -20%, rgba(${rgb.r},${rgb.g},${rgb.b},0.06), transparent)`,
            },
          },
        },
        MuiCard: {
          styleOverrides: {
            root: {
              boxShadow: '0 8px 24px rgba(2,132,199,0.08)',
              borderRadius: 16,
            },
          },
        },
        MuiButton: {
          defaultProps: { disableElevation: true },
          styleOverrides: {
            root: { borderRadius: 12 },
            containedPrimary: {
              boxShadow: `0 10px 24px rgba(${rgb.r},${rgb.g},${rgb.b},0.35)`,
            },
          },
        },
        MuiChip: {
          styleOverrides: {
            root: { borderRadius: 10, fontWeight: 600 },
          },
        },
        MuiTabs: {
          styleOverrides: {
            indicator: { height: 3, borderRadius: 3 },
          },
        },
      },
    });
  }, [scheme.primary, scheme.secondary]);

  const AppRoutes: React.FC = () => {
    const location = useLocation();

    React.useEffect(() => {
      console.log('[Router] location change:', location.pathname + location.search);
    }, [location.pathname, location.search]);

    return (
      <>
        <Layout>
          <Routes location={location} key={location.pathname}>
            <Route path="/" element={<HomePage />} />
          <Route path="/products" element={<ProductsPage />} />
          {/* Load HomePage for dynamic page paths like /page/whatever */}
          <Route path="/page/:slug" element={<HomePage />} />
          <Route
            path="/messages"
            element={
              <ProtectedRoute>
                <MessagesPage />
              </ProtectedRoute>
            }
          />
          {/* Also allow direct slugs like /test for custom pages created in the Page Builder */}
          <Route path="/:slug" element={<HomePage />} />
          <Route path="/all-products" element={<AllProductsPage />} />
          <Route path="/all-jobs" element={<AllProductsPage />} />
          <Route path="/product/:id" element={<ProductDetailPage />} />
          <Route path="/cart" element={<CartPage />} />
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route
            path="/seller"
            element={
              <ProtectedRoute>
                <SellerDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/orders"
            element={
              <ProtectedRoute>
                <OrdersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <UserDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/talent-pool"
            element={
              <ProtectedRoute>
                <TalentPoolPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/upload-product"
            element={
              <ProtectedRoute>
                <ProductUploadPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/upload-job"
            element={
              <ProtectedRoute>
                <JobUploadPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/edit-product/:id"
            element={
              <ProtectedRoute>
                <EditProductPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/edit-job/:id"
            element={
              <ProtectedRoute>
                <EditProductPage />
              </ProtectedRoute>
            }
          />
          <Route path="/seller/:id" element={<SellerProfilePage />} />
          <Route
            path="/user-profile"
            element={
              <ProtectedRoute>
                <ProfileRouter />
              </ProtectedRoute>
            }
          />
          <Route path="/user-profile/:id" element={<ProfileRouter />} />
          <Route path="/profile/:id" element={<ProfileRedirect />} />
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/media-gallery"
            element={
              <ProtectedRoute>
                <MediaGallery />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/component-editor"
            element={
              <ProtectedRoute>
                <ComponentEditor />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/page-preview"
            element={
              <PagePreview />
            }
          />
          <Route
            path="/quill-admin/page-preview"
            element={
              <PagePreview />
            }
          />
          <Route
            path="/pages-editor"
            element={
              <PagesEditor />
            }
          />
          <Route
            path="/post-editor"
            element={
              <PostEditor />
            }
          />
          <Route path="/admin-*" element={<AdminLinkPage />} />
          <Route path="/categories" element={<CategoriesPage />} />
          <Route path="/job-categories" element={<CategoriesPage />} />
          <Route
            path="/post-editor"
            element={
              <ProtectedRoute>
                <PostEditor />
              </ProtectedRoute>
            }
          />
          <Route path="/members" element={<MembersPage />} />
          <Route path="/companies" element={<CompaniesPage />} />
          <Route path="/companies/create" element={<CreateCompanyPage />} />
          <Route path="/companies/:id" element={<CompanyPage />} />
          <Route path="/companies/:slug/edit" element={<EditCompanyPage />} />
          <Route path="/payment-success" element={<PaymentSuccessPage />} />
          <Route path="/payment-cancelled" element={<PaymentCancelledPage />} />
          <Route path="/kundencenter" element={<KundencenterPage />} />
          <Route
            path="/subscriptions-platform-purchases"
            element={
              <ProtectedRoute>
                <SubscriptionsPlatformPurchasesPage />
              </ProtectedRoute>
            }
          />
          <Route path="/datenschutz" element={<PrivacyPolicyPage />} />
          <Route path="/privacy" element={<Navigate to="/datenschutz" replace />} />
          <Route path="/agb" element={<TermsPage />} />
          <Route path="/terms" element={<Navigate to="/agb" replace />} />
          <Route path="/regeln" element={<RulesPage />} />
          <Route path="/rules" element={<Navigate to="/regeln" replace />} />
          <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Layout>
      </>
    );
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <SettingsOverlay />
        <div className="App">
          <AppRoutes />
          <AdminLinkVerifierDialog />
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: { background: '#363636', color: '#fff' },
              success: {
                duration: 3000,
                iconTheme: { primary: '#4ade80', secondary: '#fff' },
              },
              error: {
                duration: 5000,
                iconTheme: { primary: '#ef4444', secondary: '#fff' },
              },
            }}
          />
        </div>
      </Router>
    </ThemeProvider>
  );
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TranslationProvider>
        <WebsiteSettingsProvider>
          <AppModeProvider>
            <ThemedApp />
          </AppModeProvider>
        </WebsiteSettingsProvider>
      </TranslationProvider>
    </QueryClientProvider>
  );
}

export default App;
